import { useMutation } from "@tanstack/react-query";
import {
  useDataProvider,
  useEditController,
  useNotify,
  useRecordContext,
  useRedirect,
  useRequireAccess,
  useTranslate,
} from "ra-core";
import type { SubmitHandler } from "react-hook-form";
import { CancelButton } from "@/components/admin/cancel-button";
import { SaveButton } from "@/components/admin/form";
import { SimpleForm } from "@/components/admin/simple-form";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileFormToolbar, MobilePage } from "../layout/MobilePage";
import type { CrmDataProvider } from "../providers/types";
import type { Sale, SalesFormData } from "../types";
import { SalesInputs } from "./SalesInputs";

function EditToolbar() {
  return (
    <div className="flex justify-end gap-4 pt-2">
      <CancelButton />
      <SaveButton />
    </div>
  );
}

/**
 * Edits a user through `dataProvider.salesUpdate` (the password never goes
 * through this form; administrators reset it from the list).
 */
export function SalesEdit() {
  // Non-admins are redirected to the access-denied page
  const { isPending: isAccessPending } = useRequireAccess({
    resource: "sales",
    action: "edit",
  });
  const { record } = useEditController<Sale>();

  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const isMobile = useIsMobile();

  const { mutate } = useMutation({
    mutationKey: ["salesUpdate", record?.id],
    mutationFn: async (data: SalesFormData) => {
      if (!record) {
        throw new Error(translate("resources.sales.edit.record_not_found"));
      }
      return dataProvider.salesUpdate(record.id, data);
    },
    onSuccess: () => {
      redirect("list", "sales");
      notify("resources.sales.edit.success");
    },
    onError: (error: Error) => {
      notify(error?.message || "resources.sales.edit.error", { type: "error" });
    },
  });

  const onSubmit: SubmitHandler<SalesFormData> = async (data) => {
    mutate(data);
  };

  if (isAccessPending) return null;

  if (isMobile) {
    return (
      <MobilePage
        title={
          record
            ? `${record.first_name} ${record.last_name}`
            : translate("resources.sales.name", { smart_count: 1 })
        }
        backTo="/sales"
      >
        <SimpleForm
          toolbar={<MobileFormToolbar />}
          onSubmit={onSubmit as SubmitHandler<any>}
          record={record}
        >
          <SalesInputs />
        </SimpleForm>
      </MobilePage>
    );
  }

  return (
    <div className="max-w-lg w-full mx-auto mt-8">
      <Card>
        <CardContent>
          <SimpleForm
            toolbar={<EditToolbar />}
            onSubmit={onSubmit as SubmitHandler<any>}
            record={record}
          >
            <SaleEditTitle />
            <SalesInputs />
          </SimpleForm>
        </CardContent>
      </Card>
    </div>
  );
}

const SaleEditTitle = () => {
  const record = useRecordContext<Sale>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <h2 className="text-lg font-semibold mb-2">
      {translate("resources.sales.edit.title", {
        name: `${record.first_name} ${record.last_name}`,
      })}
    </h2>
  );
};
